import {
    MRT_ColumnDef,
    MRT_ColumnSizingState,
    MRT_RowData,
    MRT_Updater,
    createMRTColumnHelper,
} from "material-react-table";
import { useState, useEffect, useMemo, useRef } from "react";
import { Row } from "@tanstack/react-table";
import { GridJsonAttribute } from "src/components/Grid/GridJsonAttribute";
import { Score } from "src/types";
import { ColumnsService } from "../../../../utils/ColumnsService";
import { Box, Stack, Typography } from "@mui/material";
import { JsonAttribute, AggregationType } from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { GridScoreDisplay } from "../../GridScoreDisplay";
import { GridScoreComparisonDisplay, computeScoreComparisonPercent } from "../../GridScoreComparisonDisplay";
import {
    IngredientScoreSourceMetaPayload,
    OlapScoresResult,
    resolveRowSourceMeta,
} from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ScoreSourceChip } from "src/components/Score/ScoreSourceChip";
import { formatNumber } from "src/utils/formatting";
import i18n from "src/i18n";
import type { ComponentAppearance } from "src/components/appearance";

/**
 * Formats an aggregated number using the user's locale and specified precision
 * @param value The numeric value to format
 * @param precision The number of decimal places (defaults to 2)
 * @returns Formatted number string
 */
const formatAggregatedNumber = (value: number, precision: number = 2): string => {
    const locale = i18n.resolvedLanguage || i18n.language || "en";
    const options: Intl.NumberFormatOptions = {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    };
    return formatNumber(value, locale, options);
};

export type ColumnId = string;
export type ColumnConfig = {
    visible: boolean;
    displayName: string;
};

export type ValueWithOlapScores = {
    olapScores: OlapScoresResult;
    /**
     * Optional sidecar emitted by `OlapScoresNormalizer` for entities (currently
     * Ingredient) that have a per-supplier source cascade. The grid uses it to
     * overlay a `ScoreSourceChip` next to each score cell when the value comes
     * from a reference (supplier_reference / ingredient_reference / sure).
     */
    scoreSourceMeta?: IngredientScoreSourceMetaPayload;
};

export type ColumnConfigMapping = Record<string, ColumnConfig>;

export type MrtColumns<GridValue extends MRT_RowData> = MRT_ColumnDef<GridValue, any>[];

export type GridColumnsType<GridValue extends MRT_RowData> = {
    columnOrder: string[];
    setColumnOrder: (values: string[]) => void;
    resetColumnOrder: () => void;
    columnConfig: ColumnConfigMapping;
    toggleVisibility: (columnKey: string, visible: boolean) => void;
    setColumnVisibility: (visibility: Record<string, boolean>) => void;
    replaceColumnVisibility: (visibility: Record<string, boolean>) => void;
    resetColumnVisibility: () => void;
    columnVisibility: Record<string, boolean>;
    columnSizing: Record<string, number>;
    setColumnSizing: (value: MRT_Updater<MRT_ColumnSizingState>) => void;
    setSkipViewReset: (value: boolean) => void;
    setSuppressPersist: (value: boolean) => void;

    mrt_columns: MrtColumns<GridValue>;
};

export type UseGridColumnsOptions = {
    persist?: boolean;
    initialColumnOrder?: string[];
    initialColumnVisibility?: Record<string, boolean>;
    initialColumnSizing?: Record<string, number>;
};

const mergeColumnsIntoOrder = (persistedOrder: string[], declaredOrder: string[]): string[] => [
    ...persistedOrder,
    ...declaredOrder.filter((columnId) => !persistedOrder.includes(columnId)),
];

export const useGridColumns = <GridValue extends MRT_RowData>(
    mrt_columns: MrtColumns<GridValue>,
    gridKey: string,
    options?: UseGridColumnsOptions,
): GridColumnsType<GridValue> => {
    const persist = options?.persist !== false;

    const currentInitialColumnOrder = useMemo(() => {
        return persist ? ColumnsService.getColumnOrder(gridKey) : [];
    }, [gridKey, persist]);

    const currentInitialColumnConfig = useMemo(() => {
        return persist ? ColumnsService.getColumnVisibility(gridKey) : new Map<string, boolean>();
    }, [gridKey, persist]);

    const currentColumnsSizing = useMemo(() => {
        if (persist) {
            return ColumnsService.getColumnWidth(gridKey);
        }

        return options?.initialColumnSizing ?? {};
    }, [gridKey, options?.initialColumnSizing, persist]);

    if (persist) {
        ColumnsService.registerGrid(gridKey);
    }

    const baseColumnOrder = useMemo(() => {
        return options?.initialColumnOrder ?? currentInitialColumnOrder;
    }, [currentInitialColumnOrder, options?.initialColumnOrder]);

    const initialColumnOrder = useMemo(() => {
        const declaredColumnOrder = mrt_columns.map(
            (col) => col.accessorKey ?? col.id ?? "DefaultValueThatShouldNeverHappen",
        );

        return mergeColumnsIntoOrder(baseColumnOrder, declaredColumnOrder);
    }, [baseColumnOrder, mrt_columns]);

    const initialColumnConfig = useMemo(() => {
        return mrt_columns.reduce<ColumnConfigMapping>((accColumnConfig, mrt_col) => {
            // If the default value occurs, we need to find a key that is consistent
            // inside MRT Grid
            const colId = mrt_col.accessorKey ?? mrt_col.id ?? "DefaultValueThatShouldNeverHappen";
            const hasExplicitVisibility = options?.initialColumnVisibility !== undefined;
            const defaultVisibility = (mrt_col.meta as Record<string, unknown> | undefined)?.visibleByDefault !== false;
            const isVisible =
                hasExplicitVisibility ?
                    (options?.initialColumnVisibility?.[colId] ?? false)
                :   (currentInitialColumnConfig.get(colId) ?? defaultVisibility);

            const displayName =
                typeof mrt_col.header === "string" ? mrt_col.header
                : typeof mrt_col.header === "number" ? String(mrt_col.header)
                : colId;

            accColumnConfig[colId] = {
                visible: isVisible,
                displayName: displayName,
            };

            return accColumnConfig;
        }, {});
    }, [currentInitialColumnConfig, mrt_columns, options?.initialColumnVisibility]);

    const availableColumnIds = useMemo((): string[] => {
        return mrt_columns.map((col) => col.accessorKey ?? col.id ?? "DefaultValueThatShouldNeverHappen");
    }, [mrt_columns]);

    const availableColumnIdsSignature = useMemo((): string => {
        return availableColumnIds.join("|");
    }, [availableColumnIds]);

    const areStringArraysEqual = (a: string[], b: string[]): boolean => {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    };

    const areNumberRecordsEqual = (a: Record<string, number>, b: Record<string, number>): boolean => {
        if (a === b) return true;
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const k of aKeys) {
            if (a[k] !== b[k]) return false;
        }
        return true;
    };

    const areColumnConfigMappingsEqual = (a: ColumnConfigMapping, b: ColumnConfigMapping): boolean => {
        if (a === b) return true;
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const k of aKeys) {
            const av = a[k];
            const bv = b[k];
            if (!bv) return false;
            if (av.visible !== bv.visible) return false;
            if (av.displayName !== bv.displayName) return false;
        }
        return true;
    };

    const [columnOrder, _setColumnOrder] = useState(initialColumnOrder);
    const [columnConfig, _setColumnConfig] = useState(initialColumnConfig);
    const [columnSizing, setColumnSizing] = useState(currentColumnsSizing);

    const skipViewResetRef = useRef(false);
    const suppressPersistRef = useRef(false);

    const shouldPersist = () => persist && !suppressPersistRef.current;

    const defaultColumnOrder = useMemo(() => {
        return mrt_columns.map((col) => col.accessorKey ?? col.id ?? "DefaultValueThatShouldNeverHappen");
    }, [mrt_columns]);

    const setColumnOrder = (values: string[]) => {
        _setColumnOrder(values);
        if (shouldPersist()) {
            ColumnsService.saveColumnOrder(gridKey, values);
        }
    };

    const resetColumnOrder = () => {
        _setColumnOrder(defaultColumnOrder);
        if (shouldPersist()) {
            ColumnsService.saveColumnOrder(gridKey, defaultColumnOrder);
        }
    };

    const setSkipViewReset = (value: boolean) => {
        skipViewResetRef.current = value;
    };

    const setSuppressPersist = (value: boolean) => {
        suppressPersistRef.current = value;
    };

    const toggleVisibility = (columnKey: string, visible: boolean) => {
        const configKey = columnConfig[columnKey];

        if (configKey === undefined) return;
        const newConfigKey = {
            ...configKey,
            visible: visible,
        };

        const newConfig = {
            ...columnConfig,
            [columnKey]: newConfigKey,
        };
        _setColumnConfig(newConfig);

        if (shouldPersist()) {
            const newVisibilityMap = new Map<string, boolean>(
                Object.entries(newConfig).map(([colKey, conf]) => [colKey, conf.visible]),
            );
            ColumnsService.saveColumnVisibility(gridKey, newVisibilityMap);
        }
    };

    const setColumnVisibility = (visibility: Record<string, boolean>) => {
        const newConfig = Object.entries(columnConfig).reduce<ColumnConfigMapping>((acc, [colKey, conf]) => {
            acc[colKey] = {
                ...conf,
                visible: visibility[colKey] ?? conf.visible,
            };
            return acc;
        }, {});
        _setColumnConfig(newConfig);

        if (shouldPersist()) {
            const newVisibilityMap = new Map<string, boolean>(
                Object.entries(newConfig).map(([colKey, conf]) => [colKey, conf.visible]),
            );
            ColumnsService.saveColumnVisibility(gridKey, newVisibilityMap);
        }
    };

    const replaceColumnVisibility = (visibility: Record<string, boolean>) => {
        const newConfig = Object.entries(columnConfig).reduce<ColumnConfigMapping>((acc, [colKey, conf]) => {
            acc[colKey] = {
                ...conf,
                visible: visibility[colKey] ?? false,
            };
            return acc;
        }, {});
        _setColumnConfig(newConfig);

        if (shouldPersist()) {
            const newVisibilityMap = new Map<string, boolean>(
                Object.entries(newConfig).map(([colKey, conf]) => [colKey, conf.visible]),
            );
            ColumnsService.saveColumnVisibility(gridKey, newVisibilityMap);
        }
    };

    const resetColumnVisibility = () => {
        _setColumnConfig(initialColumnConfig);

        if (shouldPersist()) {
            ColumnsService.saveColumnVisibility(gridKey, currentInitialColumnConfig);
        }
    };

    useEffect(() => {
        if (!persist) return;
        ColumnsService.saveColumnWidth(gridKey, columnSizing);
    }, [columnSizing, gridKey, persist]);

    const controlledSignature = useMemo((): string => {
        if (persist) return "persisted";

        const order = initialColumnOrder.join("|");
        const visibility = Object.entries(initialColumnConfig)
            .map(([id, conf]) => `${id}:${conf.visible ? 1 : 0}`)
            .sort()
            .join("|");
        const sizing = Object.entries(currentColumnsSizing)
            .map(([id, size]) => `${id}:${size}`)
            .sort()
            .join("|");

        return `${order}__${visibility}__${sizing}`;
    }, [currentColumnsSizing, initialColumnConfig, initialColumnOrder, persist]);

    const lastAppliedControlledSignatureRef = useRef<string | null>(null);

    const lastAppliedAvailableColumnIdsSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        if (persist) {
            if (lastAppliedAvailableColumnIdsSignatureRef.current === availableColumnIdsSignature) {
                return;
            }

            lastAppliedAvailableColumnIdsSignatureRef.current = availableColumnIdsSignature;

            if (skipViewResetRef.current) {
                skipViewResetRef.current = false;
                return;
            }

            _setColumnOrder((prev) => {
                const normalized = mergeColumnsIntoOrder(prev, availableColumnIds);
                return areStringArraysEqual(prev, normalized) ? prev : normalized;
            });

            _setColumnConfig((prev) => {
                const merged: ColumnConfigMapping = {};
                for (const mrt_col of mrt_columns) {
                    const colId = mrt_col.accessorKey ?? mrt_col.id ?? "DefaultValueThatShouldNeverHappen";
                    const existing = prev[colId];
                    if (existing !== undefined) {
                        merged[colId] = existing;
                    } else {
                        const defaultVisibility =
                            (mrt_col.meta as Record<string, unknown> | undefined)?.visibleByDefault !== false;
                        const displayName =
                            typeof mrt_col.header === "string" ? mrt_col.header
                            : typeof mrt_col.header === "number" ? String(mrt_col.header)
                            : colId;
                        merged[colId] = {
                            visible: currentInitialColumnConfig.get(colId) ?? defaultVisibility,
                            displayName,
                        };
                    }
                }
                return areColumnConfigMappingsEqual(prev, merged) ? prev : merged;
            });

            setColumnSizing((prev) =>
                areNumberRecordsEqual(prev, currentColumnsSizing) ? prev : currentColumnsSizing,
            );
            return;
        }

        if (lastAppliedControlledSignatureRef.current === controlledSignature) {
            return;
        }

        lastAppliedControlledSignatureRef.current = controlledSignature;

        _setColumnOrder((prev) => (areStringArraysEqual(prev, initialColumnOrder) ? prev : initialColumnOrder));
        _setColumnConfig((prev) =>
            areColumnConfigMappingsEqual(prev, initialColumnConfig) ? prev : initialColumnConfig,
        );
        setColumnSizing((prev) => (areNumberRecordsEqual(prev, currentColumnsSizing) ? prev : currentColumnsSizing));
    }, [
        availableColumnIdsSignature,
        controlledSignature,
        currentColumnsSizing,
        gridKey,
        initialColumnConfig,
        initialColumnOrder,
        mrt_columns.length,
        persist,
        currentInitialColumnConfig,
    ]);

    const configArray = Object.entries(columnConfig);

    const columnVisibility = configArray.reduce<Record<string, boolean>>((accVisibility, [columnName, conf]) => {
        const unknownColumnVisibility = false;
        accVisibility[columnName] = conf?.visible ?? unknownColumnVisibility;
        return accVisibility;
    }, {});

    return {
        columnConfig,
        toggleVisibility,
        setColumnVisibility,
        replaceColumnVisibility,
        resetColumnVisibility,
        columnOrder,
        setColumnOrder,
        resetColumnOrder,
        mrt_columns,
        columnVisibility,
        columnSizing,
        setColumnSizing,
        setSkipViewReset,
        setSuppressPersist,
    };
};

export const extendColumns = <GridValue extends MRT_RowData>(
    mrt_columns: MRT_ColumnDef<GridValue, string>[],
    column: MRT_ColumnDef<GridValue, string>,
    position: number,
) => {
    mrt_columns.splice(position, 0, column);
};

export const extendByColumns = <Data extends MRT_RowData>(
    mrt_columns: MRT_ColumnDef<Data, any>[],
    columns: NoInfer<MRT_ColumnDef<Data, any>[] | undefined>,
) => {
    if (columns === undefined) return mrt_columns;

    mrt_columns.splice(-1, 0, ...columns);
};

export const extendColumnsByScores = <GridValue extends ValueWithOlapScores | Required<ValueWithJsonAttributes>>(
    mrt_columns: MRT_ColumnDef<GridValue, any>[],
    data: GridValue[], // Add data parameter for aggregations
    scores: Score[],
    opts?: {
        getReferenceTitle?: (scoreTitle: string) => string;
        getComparisonTitle?: (scoreTitle: string) => string;
        enableFiltering?: boolean;
        enableSorting?: boolean;
        enableOlapSorting?: boolean;
        FilterComponent?: any; // Will be used as Filter property
        appearance?: ComponentAppearance;
    },
    // position?: number,
    // isRefetching: boolean = false,
) => {
    const newColumns: MRT_ColumnDef<GridValue, any>[] = [];
    const columnHelper = createMRTColumnHelper<GridValue>();

    for (const [i, score] of scores.entries()) {
        const scoreId = score.id;
        // Check if this score has aggregation enabled
        const hasAggregation =
            score.aggregationType && (score.aggregationType === "sum" || score.aggregationType === "average");
        const hasAggregationValue = data.some((row) => {
            const scoreData = "olapScores" in row ? row.olapScores[scoreId] : undefined;
            const value = scoreData?.score?.value;

            return value !== null && value !== undefined && !isNaN(Number(value));
        });

        // Create Footer component if aggregation is enabled
        const FooterComponent =
            hasAggregation && hasAggregationValue ?
                () => {
                    const aggregatedValue = useMemo(() => {
                        if (!data || data.length === 0) return null;

                        const values = data
                            .map((row) => {
                                const scoreData = "olapScores" in row ? row?.olapScores[scoreId] : undefined;

                                return scoreData?.score?.value;
                            })
                            .filter((value) => value !== null && value !== undefined && !isNaN(Number(value)))
                            .map((value) => Number(value));

                        if (values.length === 0) return null;

                        let result: number;

                        switch (score.aggregationType) {
                            case "sum":
                                result = values.reduce((sum, val) => sum + val, 0);
                                break;
                            case "average":
                                result = values.reduce((sum, val) => sum + val, 0) / values.length;
                                break;
                            default:
                                return null;
                        }

                        return {
                            value: result,
                            count: values.length,
                            total: data.length,
                        };
                    }, [data]);

                    if (!aggregatedValue) return null;

                    return (
                        <Stack spacing={0.5}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="h3">
                                    {formatAggregatedNumber(aggregatedValue.value, score.precision || 2)}
                                </Typography>
                                {score.unit?.abbreviation && (
                                    <Typography
                                        variant="body"
                                        sx={{ marginLeft: "4px", fontWeight: 400, color: "rgb(163 183 160)" }}
                                    >
                                        {score.unit.abbreviation}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    );
                }
            :   undefined;

        const scoreTitle = opts?.appearance === "product-v2" ? score.title : score.title.replace(/ /g, "\u00a0");
        const newColScore = columnHelper.accessor(
            (row) => ("olapScores" in row ? row?.olapScores[scoreId] : undefined),
            {
                id: `score-${scoreId}`,
                header: scoreTitle,

                enableSorting: opts?.enableOlapSorting ?? false,
                enableColumnFilter: opts?.enableFiltering ?? false,
                ...(opts?.FilterComponent ? { Filter: opts.FilterComponent } : {}),
                Cell: ({ row: { original } }) => {
                    const scoreData = "olapScores" in original ? original.olapScores[scoreId] : undefined;
                    const scoreValue = scoreData?.score.value;

                    const numericalScoreValue = OlapScoresService.normalizeNumericalScore(scoreValue);
                    // The OLAP collection response carries `scoreSourceMeta` for Ingredient
                    // rows (cf. `OlapScoresNormalizer`). The badge is *only* shown when the
                    // displayed value comes from a reference path — never when an SSQ
                    // contributed (precise, questionnaire-backed value). `resolveRowSourceMeta`
                    // encodes that rule centrally; see `olapScoresSchema.ts`.
                    const payload =
                        "scoreSourceMeta" in original ? (original as ValueWithOlapScores).scoreSourceMeta : undefined;
                    const sourceMeta = resolveRowSourceMeta(payload);

                    return (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                            <GridScoreDisplay
                                value={numericalScoreValue}
                                unit={score.unit?.abbreviation ?? ""}
                                precision={score.precision ?? 2}
                                appearance={opts?.appearance}
                            />
                            {numericalScoreValue !== null && sourceMeta && (
                                <ScoreSourceChip meta={sourceMeta} compact />
                            )}
                        </Box>
                    );
                },
                sortingFn: (rowA, rowB) => {
                    return sortGridRowsByScores<GridValue>(scoreId, rowA, rowB);
                },
                Footer: FooterComponent,
            },
        );
        newColumns.push(newColScore);

        const refTitle = opts?.getReferenceTitle?.(scoreTitle);
        const withReference = refTitle !== undefined && hasReferenceScore(score, data);
        if (withReference) {
            const refScoreColumn = columnHelper.accessor(
                (row) => {
                    return "jsonAttributes" in row ? row.jsonAttributes : undefined;
                },
                {
                    id: `referenceScore-${scoreId}`,
                    header: refTitle,

                    enableSorting: opts?.enableSorting ?? false,
                    enableColumnFilter: opts?.enableFiltering ?? false,
                    ...(opts?.FilterComponent ? { Filter: opts.FilterComponent } : {}),
                    Cell: ({ row: { original } }) => {
                        const jsonAttributes = "jsonAttributes" in original ? original.jsonAttributes : undefined;

                        const value =
                            jsonAttributes !== undefined && "referenceScores" in jsonAttributes ?
                                jsonAttributes["referenceScores"][score.id]
                            :   undefined;

                        return (
                            <GridScoreDisplay
                                value={value}
                                unit={score.unit?.abbreviation ?? ""}
                                precision={score.precision ?? 2}
                                appearance={opts?.appearance}
                            />
                        );
                    },
                    // Footer: FooterComponent,
                },
            );
            newColumns.push(refScoreColumn);

            const comparisonTitle = opts?.getComparisonTitle?.(scoreTitle);
            if (comparisonTitle !== undefined) {
                const comparisonColumn = columnHelper.accessor(
                    (row) => {
                        const scoreData = "olapScores" in row ? row?.olapScores[scoreId] : undefined;
                        const scoreValue = OlapScoresService.normalizeNumericalScore(scoreData?.score.value);
                        const jsonAttributes = "jsonAttributes" in row ? row.jsonAttributes : undefined;
                        const refScoreValue =
                            jsonAttributes !== undefined && "referenceScores" in jsonAttributes ?
                                jsonAttributes["referenceScores"][score.id]
                            :   undefined;
                        return computeScoreComparisonPercent(scoreValue, refScoreValue);
                    },
                    {
                        id: `scoreComparison-${scoreId}`,
                        header: comparisonTitle,
                        meta: { visibleByDefault: false },

                        enableSorting: opts?.enableSorting ?? false,
                        enableColumnFilter: opts?.enableFiltering ?? false,
                        ...(opts?.FilterComponent ? { Filter: opts.FilterComponent } : {}),
                        Cell: ({ row: { original } }) => {
                            const scoreData = "olapScores" in original ? original.olapScores[scoreId] : undefined;
                            const scoreValue = OlapScoresService.normalizeNumericalScore(scoreData?.score.value);
                            const jsonAttributes = "jsonAttributes" in original ? original.jsonAttributes : undefined;
                            const refScoreValue =
                                jsonAttributes !== undefined && "referenceScores" in jsonAttributes ?
                                    jsonAttributes["referenceScores"][score.id]
                                :   undefined;
                            return (
                                <GridScoreComparisonDisplay
                                    scoreValue={scoreValue}
                                    refScoreValue={refScoreValue}
                                    precision={0}
                                />
                            );
                        },
                        sortingFn: (rowA, rowB) => {
                            const getPercent = (row: typeof rowA) => {
                                const scoreData =
                                    "olapScores" in row.original ? row.original.olapScores[scoreId] : undefined;
                                const scoreValue = OlapScoresService.normalizeNumericalScore(scoreData?.score.value, 0);
                                const jsonAttributes =
                                    "jsonAttributes" in row.original ? row.original.jsonAttributes : undefined;
                                const refScoreValue =
                                    jsonAttributes !== undefined && "referenceScores" in jsonAttributes ?
                                        jsonAttributes["referenceScores"][score.id]
                                    :   undefined;
                                return computeScoreComparisonPercent(scoreValue, refScoreValue) ?? 0;
                            };
                            return getPercent(rowA) - getPercent(rowB);
                        },
                    },
                );
                newColumns.push(comparisonColumn);
            }
        }
    }

    extendByColumns(mrt_columns, newColumns);
};

export const sortGridRowsByScores = <GridValue extends ValueWithOlapScores | Required<ValueWithJsonAttributes>>(
    scoreId: number,
    rowA: Row<GridValue>,
    rowB: Row<GridValue>,
) => {
    if ("jsonAttributes" in rowA.original || "jsonAttributes" in rowB.original) {
        // [ds] no scores sorting for now anyway
        return 0;
    }

    const scoreA = rowA.original.olapScores[scoreId];
    const scoreB = rowB.original.olapScores[scoreId];

    const scoreAValue = OlapScoresService.normalizeNumericalScore(scoreA?.score.value);
    const scoreBValue = OlapScoresService.normalizeNumericalScore(scoreB?.score.value);

    if (scoreAValue === null && scoreBValue === null) return 0;
    if (scoreAValue === null) return 1;
    if (scoreBValue === null) return -1;

    return scoreBValue - scoreAValue;
};

const hasReferenceScore = <GridValue extends ValueWithOlapScores | Required<ValueWithJsonAttributes>>(
    score: Score,
    data: GridValue[],
): boolean => {
    return data.some((row) => {
        const jsonAttributes = "jsonAttributes" in row ? (row as ValueWithJsonAttributes).jsonAttributes : undefined;
        if (!jsonAttributes || !("referenceScores" in jsonAttributes)) return false;
        return jsonAttributes["referenceScores"][score.id] !== undefined;
    });
};

type ValueWithJsonAttributes = {
    jsonAttributes?: Record<string, any>;
};

export const extendColumnsByJsonAttributes = <GridValue extends ValueWithJsonAttributes>(
    mrt_columns: MRT_ColumnDef<GridValue, string>[],
    jsonAttributeDefinitions: JsonAttribute[],
    data?: GridValue[],
    options?: { appearance?: ComponentAppearance },
) => {
    const columnHelper = createMRTColumnHelper<GridValue>();
    const columnsLength = mrt_columns.length;

    for (const [i, definition] of jsonAttributeDefinitions.entries()) {
        const attributeId = definition.identifier;
        const attributeAccessorKey = `jsonAttribute-${attributeId}`;

        // Check if this attribute has aggregation enabled
        const hasAggregation =
            definition.aggregationType &&
            definition.aggregationType !== AggregationType.NONE &&
            (definition.type === "int" || definition.type === "float");
        const hasAggregationValue =
            data?.some((row) => {
                const value = row.jsonAttributes?.[attributeId];

                return value !== null && value !== undefined && !isNaN(Number(value));
            }) === true;

        // Create Footer component if aggregation is enabled
        const FooterComponent =
            hasAggregation && hasAggregationValue && data ?
                () => {
                    const aggregatedValue = useMemo(() => {
                        if (!data || data.length === 0) return null;

                        const values = data
                            .map((row) => row.jsonAttributes?.[attributeId])
                            .filter((value) => value !== null && value !== undefined && !isNaN(Number(value)))
                            .map((value) => Number(value));

                        if (values.length === 0) return null;

                        let result: number;
                        let label: string;

                        switch (definition.aggregationType) {
                            case AggregationType.SUM:
                                result = values.reduce((sum, val) => sum + val, 0);
                                label = "Somme";
                                break;
                            case AggregationType.AVERAGE:
                                result = values.reduce((sum, val) => sum + val, 0) / values.length;
                                label = "Moyenne";
                                break;
                            default:
                                return null;
                        }

                        return {
                            value: result,
                            label: label,
                            count: values.length,
                            total: data.length,
                        };
                    }, [data]);

                    if (!aggregatedValue) return null;

                    return (
                        <Stack spacing={0.5}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="h3">{formatAggregatedNumber(aggregatedValue.value, 2)}</Typography>
                            </Box>
                        </Stack>
                    );
                }
            :   undefined;

        extendColumns(
            mrt_columns,
            columnHelper.accessor((row) => row.jsonAttributes?.[attributeId], {
                id: attributeAccessorKey,
                header:
                    options?.appearance === "product-v2" ? definition.label : definition.label.replace(/ /g, "\u00a0"),
                enableSorting: false,
                Cell: (value) => {
                    return (
                        <GridJsonAttribute
                            value={value.renderedCellValue}
                            attributeType={definition.type}
                            appearance={options?.appearance}
                        />
                    );
                },
                Footer: FooterComponent,
            }),
            columnsLength + i,
        );
    }
};
